import React from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../utils/cn';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

const toastStyles: Record<ToastType, { border: string; icon: React.ReactNode }> = {
  success: {
    border: 'border-action-200 bg-action-50 text-action-900',
    icon: <CheckCircle2 className="h-4 w-4 text-action-700" />,
  },
  error: {
    border: 'border-danger-100 bg-danger-50 text-danger-700',
    icon: <AlertCircle className="h-4 w-4 text-danger-600" />,
  },
  info: {
    border: 'border-ink-200 bg-white text-ink-900',
    icon: <Info className="h-4 w-4 text-academy-600" />,
  },
};

const notificationPrefixPattern = /^(?:\u2705|\u274c|\u26a0\ufe0f?|\ud83d\udd04|\u2713)\s*/u;

function normalizeToastMessage(message: string) {
  return message.replace(notificationPrefixPattern, '').trim();
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const nextIdRef = React.useRef(1);
  const timersRef = React.useRef<Map<number, number>>(new Map());

  const removeToast = React.useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }

    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = React.useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = nextIdRef.current++;
      setToasts((current) => [...current, { id, message: normalizeToastMessage(message), type }]);

      const timer = window.setTimeout(() => {
        timersRef.current.delete(id);
        setToasts((current) => current.filter((toast) => toast.id !== id));
      }, 3500);

      timersRef.current.set(id, timer);
    },
    [],
  );

  React.useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  const value = React.useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-3 top-3 z-[70] flex flex-col gap-2 sm:left-auto sm:right-4 sm:top-4 sm:w-full sm:max-w-sm">
        {toasts.map((toast) => {
          const style = toastStyles[toast.type];
          return (
            <div
              key={toast.id}
              role={toast.type === 'error' ? 'alert' : 'status'}
              aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
              className={cn(
                'pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-raised',
                style.border,
              )}
            >
              <div className="mt-0.5">{style.icon}</div>
              <p className="flex-1 text-sm leading-5">{toast.message}</p>
              <Button
                variant="ghost"
                size="icon"
                icon={<X className="h-4 w-4" />}
                onClick={() => removeToast(toast.id)}
                aria-label="Dismiss toast"
              />
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }

  return context;
}
